// Pop-out menus (02-building-blocks.md): next to what opened them, in the top layer (the popover
// API, which also closes them on Escape and a press outside). ↑/↓ and Tab move, Enter or Space
// runs an entry; choosing closes the menu first, then runs it; the focus goes back to the opener.
import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';
import { Icon } from './icons';

const CloseMenu = createContext<() => void>(() => {});

export interface MenuProps {
  /** The accessible name. */
  label: string;
  /** Opened from a button: below it, aligned to its end. */
  anchor?: HTMLElement | null;
  /** Opened by a right-click: at the pointer. */
  point?: { x: number; y: number };
  /** Beside its opener (a rail button or a submenu), top-aligned; or above it, start-aligned (the phone's "+", 15). */
  side?: 'below' | 'right' | 'above';
  /** Inside a sheet (a Dialog, whose scrim takes the presses outside): no popover, no placing. */
  inline?: boolean;
  className?: string;
  onClose: () => void;
  children: ReactNode;
  /** A test hook on the menu (90-test-hooks.md). */
  [hook: `data-desyr-${string}`]: string | undefined;
}

const items = (menu: HTMLElement) => [...menu.querySelectorAll<HTMLElement>(':scope > [role^="menuitem"]:not([disabled]), :scope > [role="group"] > [role^="menuitem"]:not([disabled])')];

export function Menu({ label, anchor, point, side = 'below', inline, className, onClose, children, ...hooks }: MenuProps) {
  const ref = useRef<HTMLDivElement>(null);
  const closing = useRef(onClose);
  closing.current = onClose;

  useLayoutEffect(() => {
    const menu = ref.current!;
    if (inline) return void menu.focus();
    const opener = (anchor ?? document.activeElement) as HTMLElement | null;
    menu.showPopover?.();
    const m = menu.getBoundingClientRect();
    let x = point?.x ?? 0;
    let y = point?.y ?? 0;
    if (anchor) {
      const r = anchor.getBoundingClientRect();
      [x, y] = side === 'right' ? [r.right + 4, r.top] : side === 'above' ? [r.left, r.top - m.height - 4] : [r.right - m.width, r.bottom + 4];
    }
    menu.style.left = `${Math.max(16, Math.min(x, innerWidth - m.width - 16))}px`;
    menu.style.top = `${Math.max(16, Math.min(y, innerHeight - m.height - 16))}px`;
    menu.focus();
    const onToggle = (e: Event) => (e as ToggleEvent).newState === 'closed' && closing.current();
    menu.addEventListener('toggle', onToggle);
    return () => {
      menu.removeEventListener('toggle', onToggle);
      if (menu.matches?.(':popover-open')) menu.hidePopover();
      opener?.focus?.();
    };
  }, []);

  const onKeyDown = (e: React.KeyboardEvent) => {
    const list = items(ref.current!);
    const at = list.indexOf(document.activeElement as HTMLElement);
    const down = e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey);
    const up = e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey);
    if ((!down && !up) || !list.length) return;
    e.preventDefault();
    e.stopPropagation(); // a submenu inside keeps its keys
    const n = list.length;
    list[at < 0 ? (down ? 0 : n - 1) : (at + (down ? 1 : -1) + n) % n].focus();
  };

  return (
    <CloseMenu.Provider value={() => closing.current()}>
      <div ref={ref} className={className ? `menu ${className}` : 'menu'} role="menu" aria-label={label} popover={inline ? undefined : 'auto'} tabIndex={-1} onKeyDown={onKeyDown} {...hooks}>
        {children}
      </div>
    </CloseMenu.Provider>
  );
}

export interface MenuItemProps {
  icon?: string;
  destructive?: boolean;
  disabled?: boolean;
  /** The current choice in a choice menu. */
  pressed?: boolean;
  /** An accessible name other than the text (a quick reaction's shortcode). */
  label?: string;
  /** Why it can't be used: a tooltip and the accessible description. */
  title?: string;
  onSelect: () => void;
  children: ReactNode;
}

export function MenuItem({ icon, destructive, disabled, pressed, label, title, onSelect, children }: MenuItemProps) {
  const close = useContext(CloseMenu);
  return (
    <button
      type="button"
      role="menuitem"
      className={destructive ? 'menu-item destructive' : 'menu-item'}
      disabled={disabled}
      aria-pressed={pressed}
      aria-label={label}
      title={title}
      aria-description={title}
      onClick={() => {
        close();
        onSelect();
      }}
    >
      {icon && <Icon svg={icon} size={16} />}
      <span className="menu-label">{children}</span>
    </button>
  );
}

export const MenuLine = () => <div role="separator" className="menu-line" />;
